import React, { Fragment, useContext, useEffect, useRef } from "react";
import { EChartsOption } from "echarts-for-react";
import { init } from "echarts";
import { isNumber } from "lodash";
import PrintContext, { PrintContextValue } from "./PrintContext";

type EChartProps = {
  option: EChartsOption;
  height: number;
};

export default function EChart(props: EChartProps) {
  const print = useContext(PrintContext);
  if (print) return <EChartSsr {...props} print={print} />;
  else return <EchartDom {...props} />;
}

type EChartSsrProps = EChartProps & { print: PrintContextValue };

function EChartSsr(props: EChartSsrProps) {
  const { option, print, height } = props;

  const widthInPx = toPixels(print.width);
  const marginInPx = toPixels(print.margin);
  const width = widthInPx - marginInPx * 2;
  const chartWidth = width * 0.6;

  const chart = init(null, null, {
    renderer: "svg", // must use SVG rendering mode
    ssr: true, // enable SSR
    width: chartWidth,
    height: height,
  });

  chart.setOption(cleanOption(option));
  chart.setOption({ animation: false });

  const svgStr = chart.renderToSVGString();
  chart.dispose();
  const dataUri = `data:image/svg+xml;base64,${utf8ToBase64(svgStr)}`;
  return (
    <div style={{ display: "flex" }}>
      <img src={dataUri} style={{ width: chartWidth, height: height }} />
      <div
        style={{
          flexGrow: 1,
          height: height,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        {option.series.map((s: any, i: number) => {
          return (
            <div
              key={i}
              style={{ fontSize: option.series.length > 20 ? "0.75em" : "1em" }}
            >
              <span
                style={{
                  background: s.itemStyle.color,
                  display: "inline-block",
                  borderRadius: "0.1cm",
                  width: "1em",
                  height: "1em",
                  marginRight: "0.2cm",
                  verticalAlign: "middle",
                }}
              ></span>
              {s.name}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EchartDom(props: EChartProps) {
  const { option, height } = props;
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const chart = init(ref.current);
    chart.setOption(cleanOption(option));
    return () => chart.dispose();
  }, [option]);

  return (
    <Fragment>
      <div style={{ display: "flex" }}>
        <div ref={ref} style={{ width: "50%", height: height }} />
        <div style={{ width: "50%", height: height }}></div>
      </div>
    </Fragment>
  );
}

function utf8ToBase64(str: string) {
  const bytes = new TextEncoder().encode(str);
  let binary = "";
  bytes.forEach((b) => (binary += String.fromCharCode(b)));
  return btoa(binary);
}

function toPixels(size: string) {
  if (size.endsWith("cm")) {
    const cm = parseFloat(size);
    return cm * 37.795275591; // 1 cm = 37.795275591 px
  } else {
    throw new Error(`Unsupported size format: ${size}`);
  }
}

function cleanOption(option: EChartsOption): EChartsOption {
  option.series = option.series.filter((s: any) => !isEmptyData(s.data));
  return option;
}

function isEmptyData(input: Array<null | number>) {
  return input.filter(isNumber).length === 0;
}
